import { FormProvider, useForm, useWatch } from "react-hook-form";
import { DrawerWithStepper } from "../../../../components/DrawerWithStepper";
import { useSettingsAIChatbotContext } from "../../sections/SettingsAIChatBot";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { TestIDs } from "../../../../tests/types/testIds";
import { FaqPair, formatFaqPairs, providerLabelByModel } from "./chatbot.utils";
import { UpdateChatbotDto } from "../../../../model/chatbot/dto/UpdateChatbotDto";
import isEmpty from "lodash/isEmpty";
import { SettingsAIChatBotStepEssentials } from "./steps/SettingsAIChatBotStepEssentials";
import { StepsProps } from "../../../../utils/local-antd";
import { SettingsAIChatBotStepBasicInfo } from "./steps/SettingsAIChatBotStepBasicInfo";
import { SettingsAIChatBotStepData } from "./steps/SettingsAIChatBotStepData";
import { SettingsAIChatBotStepExtraActions } from "./steps/SettingsAIChatBotStepExtraActions";
import { chatbotActions } from "../../../../redux/chatbot/chatbot.slice";
import { AtiraToast } from "../../../../utils/AtiraToast";
import { onSetTypedCRUDErrors } from "../../../../utils/onSetTypedCRUDErrors";
import { CreateChatbotDto } from "../../../../model/chatbot/dto/CreateChatbotDto";
import { ChatbotPosition } from "../../../../model/chatbot/types/Chatbot.position.enum";
import { ChatbotAssistantKind } from "../../../../model/chatbot/types/Chatbot.assistant-kind.enum";
import { SettingsAIChatBotStepCustomization } from "./steps/SettingsAIChatBotStepCustomization";

type FormValues = UpdateChatbotDto & { aiFaqPairs: FaqPair[] };

export const SettingsAIChatBotCreateEditDrawer = () => {
  const [loading, setLoading] = useState(false);

  const { createEditDrawerOpen: open, toggleCreateEditDrawerOpen: onClose } =
    useSettingsAIChatbotContext();

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { currentBot, setCurrentBot } = useSettingsAIChatbotContext();
  const formMethods = useForm<FormValues>();
  const { control, getValues, setError, reset, setValue } = formMethods;

  const aiFaqPairs = useWatch({ control, name: "aiFaqPairs" });
  const editMode = !isEmpty(currentBot) && !isEmpty(currentBot?._id);

  const stepperItems: StepsProps["items"] = [
    { title: t("settings.aiChatbot.sections.essentials.title") },
    { title: t("settings.aiChatbot.sections.info.title") },
    { title: t("settings.aiChatbot.sections.data.title") },
    { title: t("settings.aiChatbot.sections.extraActions.title") },
    { title: t("settings.aiChatbot.sections.customization.title") },
  ].filter(Boolean) as StepsProps["items"];

  const onCreate = async (dto: CreateChatbotDto) => {
    await dispatch(chatbotActions.createChatbot(dto)).unwrap();
    AtiraToast.success(t("settings.aiChatbot.createSuccess"));
  };

  const onUpdate = async (dto: UpdateChatbotDto) => {
    await dispatch(chatbotActions.updateChatbot(dto)).unwrap();
    AtiraToast.success(t("settings.aiChatbot.updateSuccess"));
  };

  const onSubmit = async () => {
    try {
      setLoading(true);
      const dto = getValues();

      dto.aiConfig.aiFAQs = formatFaqPairs(dto.aiFaqPairs);
      dto.aiConfig.aiProvider =
        providerLabelByModel.get(dto.aiConfig.aiModel) || "";

      if (editMode) {
        await onUpdate(dto);
      } else {
        await onCreate(dto);
      }

      await dispatch(
        chatbotActions.getChatbots({ userId: user?._id }),
      ).unwrap();

      localOnClose();
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  const localOnClose = () => {
    onClose();
    reset();
    setCurrentBot(null);
  };

  useEffect(() => {
    setValue("aiConfig.aiFAQs", formatFaqPairs(aiFaqPairs), {
      shouldDirty: true,
    });
  }, [aiFaqPairs, setValue]);

  useEffect(() => {
    const aiFaqPairs = currentBot?.aiConfig?.aiFAQs?.length
      ? currentBot.aiConfig?.aiFAQs?.map(([question, answer]) => ({
          question,
          answer,
        }))
      : [{ question: "", answer: "" }];

    reset({
      aiConfig: {
        aiModel: currentBot?.aiConfig.aiModel || "",
        aiKey: currentBot?.aiConfig.aiKeyShort || "",
        aiPromptTemplate: currentBot?.aiConfig.aiPromptTemplate || "",
        aiCrawledDataSources: currentBot?.aiConfig.aiCrawledDataSources?.length
          ? currentBot.aiConfig.aiCrawledDataSources
          : [""],
        aiFAQs: currentBot?.aiConfig.aiFAQs || [],
        aiProvider: currentBot?.aiConfig.aiProvider || "",
        aiAssistantKind:
          currentBot?.aiConfig.aiAssistantKind ||
          ChatbotAssistantKind.LEAD_COLLECTOR,
      },
      aiFaqPairs,
      config: currentBot?.config || {
        personality: "",
        voice: "",
        picture: "",
        position: "" as ChatbotPosition,
        color: "",
        textBoxPlaceholder: "",
        requireFormFillingBeforeChatting: false,
      },
      formId: currentBot?.formId,
      name: currentBot?.name || "",
      description: currentBot?.description || "",
      userId: user?._id,
      chatbotId: currentBot?._id || "",
      extraActions: currentBot?.extraActions || {
        whatsApp: {
          phoneNumber: "",
          starterMessage: "",
        },
        leadSummary: {
          summaryField: "",
        },
      },
    });
  }, [currentBot, reset, user?._id]);

  return (
    <FormProvider {...formMethods}>
      <DrawerWithStepper
        open={open}
        onClose={localOnClose}
        title={
          t(editMode ? "common.edit" : "common.create") +
          " " +
          t("settings.aiChatbot.title")
        }
        submitLoading={loading}
        onSubmit={onSubmit}
        stepperItems={stepperItems}
        steps={[
          <SettingsAIChatBotStepEssentials />,
          <SettingsAIChatBotStepBasicInfo />,
          <SettingsAIChatBotStepData />,
          <SettingsAIChatBotStepExtraActions />,
          <SettingsAIChatBotStepCustomization />,
        ].filter(Boolean)}
        editMode={editMode}
        nextButtonDataTestId={
          TestIDs.chatbot.drawer.createEdit.navigation.nextButton
        }
        prevButtonDataTestId={
          TestIDs.chatbot.drawer.createEdit.navigation.backButton
        }
      />
    </FormProvider>
  );
};
